import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { pathOf, safeReturnTo } from "@/lib/returnTo";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { T, F, Over, Wordmark, th as palette } from "@/lib/bricks";
import { lockedReason } from "@/lib/legalDocs";
import LegalDocSheet from "@/components/legal/LegalDocSheet";
import { LegalDocRows, useLegalReads } from "@/components/legal/LegalDocRows";

const DOCS = ["terms", "privacy"];

/**
 * One-time terms + privacy agreement for any signed-in account that hasn't
 * agreed yet (Google, Apple, passkey, phone, and accounts made before the
 * sign-up checkbox). The API refuses other requests until this is done and
 * stamps users.terms_accepted_at with its own clock.
 *
 * Both documents open in a pop-up; the checkbox stays locked until each has
 * been scrolled to the end in this session.
 */
export default function AcceptTermsPage() {
  const { user, acceptTerms, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const p = palette(resolvedTheme === "dark");
  const legal = useLegalReads(DOCS);
  const [checked, setChecked] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const agreed = legal.done && checked;
  const why = showWhy ? lockedReason(DOCS, legal.read) : "";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!legal.done) { setShowWhy(true); return; }
    if (!agreed || busy) return;
    setError(""); setBusy(true);
    try {
      const next = await acceptTerms();
      // The gate shows in place of the page asked for; stay on it (deep links survive).
      navigate(next?.needs_onboarding ? "/onboarding" : (safeReturnTo(pathOf(location)) || "/"), { replace: true });
    } catch {
      setError("We couldn't save that just now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const blocked = !agreed || busy;
  const primary = {
    padding: "13px 20px", background: T.brick, color: "#fff", border: "none", borderRadius: 2, fontSize: 14,
    fontWeight: 500, fontFamily: F.display, width: "100%", cursor: blocked ? "not-allowed" : "pointer", opacity: blocked ? 0.6 : 1,
  };
  const link = { background: "none", border: "none", cursor: "pointer", color: p.ink, textDecoration: "underline", textUnderlineOffset: 3, fontSize: 12, padding: 0, fontFamily: F.display };

  return (
    <div data-testid="accept-terms-page" className="px-5 py-8 md:px-14 md:py-12"
      style={{ minHeight: "100vh", background: p.bg, color: p.ink, fontFamily: F.display, display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
      <Wordmark size={19} color={p.ink} />
      <form onSubmit={submit} style={{ marginTop: "auto", marginBottom: "auto", paddingTop: 40, maxWidth: 440, width: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
        <Over color={p.ink4}>One more step</Over>
        <h1 className="text-[32px] md:text-[40px]" style={{ fontWeight: 500, letterSpacing: -1.1, lineHeight: 1.08, margin: 0 }}>
          Agree to the terms and privacy policy
        </h1>
        <p style={{ fontSize: 14, color: p.ink3, margin: 0, lineHeight: 1.55 }}>
          {user?.email ? `You're signed in as ${user.email}. ` : ""}
          Before you use Bricks, please read and agree to how the service works and how we handle your data. You only need to do this once.
        </p>
        <LegalDocRows docs={DOCS} read={legal.read} onOpen={legal.setOpen} colors={p} />
        <label onClick={() => { if (!legal.done) setShowWhy(true); }}
          style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: legal.done ? "pointer" : "not-allowed", userSelect: "none", padding: "14px 16px", border: `1px solid ${p.hairS}`, borderRadius: 2 }}>
          <input data-testid="accept-terms-checkbox" type="checkbox" checked={agreed} aria-disabled={!legal.done}
            aria-describedby={why ? "accept-terms-why" : undefined}
            onChange={(e) => { if (!legal.done) { setShowWhy(true); return; } setChecked(e.target.checked); }}
            style={{ width: 16, height: 16, marginTop: 2, accentColor: p.ink, cursor: legal.done ? "pointer" : "not-allowed", flexShrink: 0, opacity: legal.done ? 1 : 0.4 }} />
          <span style={{ fontSize: 14, color: legal.done ? p.ink2 : p.ink3, lineHeight: 1.45 }}>I agree to the terms of service and the privacy policy.</span>
        </label>
        {why && <p id="accept-terms-why" role="status" data-testid="accept-terms-why" style={{ fontSize: 13, color: p.ink2, margin: "-6px 0 0", lineHeight: 1.45 }}>{why}</p>}
        <LegalDocSheet doc={legal.open} read={legal.open ? !!legal.read[legal.open] : false} onRead={legal.markRead} onClose={() => legal.setOpen(null)} colors={p} />
        {error && <p role="alert" data-testid="accept-terms-error" style={{ fontSize: 13, color: T.bad, margin: 0 }}>{error}</p>}
        <button type="submit" data-testid="accept-terms-submit" disabled={blocked} style={primary}>
          {busy ? "…" : "Agree and continue"}
        </button>
        <div style={{ fontSize: 12 }}>
          <button type="button" onClick={async () => { await logout(); navigate("/login", { replace: true }); }} style={link}>
            Sign out
          </button>
        </div>
      </form>
    </div>
  );
}
